import React, { useMemo } from 'react';

import { css } from '@betterment/samba-styled-system/css';
import { token } from '@betterment/samba-styled-system/tokens';
import { AxisBottom, TickRendererProps } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { AreaClosed } from '@visx/shape';
import { curveCatmullRom } from 'd3-shape';

type DataPoint = {
  x: number;
  y: number;
};

const data = [
  { x: 2, y: 9.4 },
  { x: 3, y: 9.95 },
  { x: 4, y: 9.88 },
  { x: 5, y: 9.05 },
  { x: 6, y: 9.48 },
  { x: 7, y: 10.83 },
  { x: 8, y: 11.73 },
  { x: 9, y: 11.83 },
  { x: 10, y: 11.77 },
  { x: 11, y: 11.24 },
  { x: 12, y: 11.38 },
  { x: 13, y: 11.7 },
  { x: 14, y: 11.11 },
  { x: 15, y: 11.23 },
  { x: 16, y: 11.33 },
  { x: 17, y: 12.45 },
  { x: 18, y: 12.26 },
  { x: 19, y: 12.58 },
  { x: 20, y: 12.6 },
  { x: 21, y: 11.95 },
  { x: 22, y: 12.58 },
  { x: 23, y: 13.1 },
  { x: 24, y: 13.32 },
  { x: 27, y: 13.64 },
  { x: 29, y: 10.9 },
  { x: 30, y: 11.13 },
  { x: 31, y: 11.45 },
  { x: 32, y: 11.5 },
  { x: 33, y: 13.26 },
  { x: 34, y: 13.74 },
  { x: 35, y: 13.0 },
  { x: 36, y: 14.29 },
  { x: 37, y: 14.26 },
  { x: 38, y: 14.72 },
  { x: 39, y: 15.0 },
];

type ChartVariant = 'loading' | 'zero-state';

type Props = {
  height: number;
  width: number;
  variant: ChartVariant;
};

const defaultMargin = { top: 10, right: 1, bottom: 16, left: 1 };

const xAccessor = (d: DataPoint) => d.x;
const yAccessor = (d: DataPoint) => d.y;

const CustomTickBottom = ({ x, transform }: TickRendererProps) => {
  const height = 8;
  const width = 24;

  return (
    <Group transform={`translate(${x}, ${height})`} alignmentBaseline="middle">
      <rect width={width} height={height} fill={token('colors.background.shimmer.base')} transform={transform} />
    </Group>
  );
};

function chartColor(variant: ChartVariant) {
  switch (variant) {
    case 'zero-state':
      return token('colors.background.accent.grey');
    default:
      return token('colors.background.shimmer.base');
  }
}

export const PlaceholderPerformanceChart: React.FC<Props> = ({ width, height, variant }) => {
  const xMax = Math.max(0, width - defaultMargin.left - defaultMargin.right);
  const yMax = Math.max(0, height - defaultMargin.top - defaultMargin.bottom);
  const performanceScale = useMemo(
    () =>
      scaleLinear({
        range: [yMax, 0],
        domain: [5, Math.max(...data.map(d => d.y))],
        nice: true,
      }),
    [yMax]
  );

  const dateScale = useMemo(
    () =>
      scaleTime({
        range: [0, xMax],
        domain: [Math.min(...data.map(xAccessor)), Math.max(...data.map(xAccessor))],
      }),
    [xMax]
  );

  return (
    <svg data-testid="loading-account-performance-chart" width={width} height={height}>
      <rect x={0} y={0} width={width} height={height} fill={token('colors.background.surface')} />
      <Group left={defaultMargin.left} top={defaultMargin.top}>
        <GridRows
          scale={performanceScale}
          width={xMax}
          height={yMax}
          strokeDasharray="4,4"
          stroke={token('colors.border.weak')}
          pointerEvents="none"
          numTicks={4}
        />

        {/* X Axis */}
        <AxisBottom
          scale={dateScale}
          top={yMax}
          tickValues={[xAccessor(data[0]), xAccessor(data[data.length - 1])]}
          hideTicks={true}
          axisLineClassName={css({ stroke: 'border.weak' })}
          tickComponent={CustomTickBottom}
          tickLabelProps={(_, index) => {
            return {
              transform: index === 0 ? `translate(0, 0)` : `translate(-24, 0)`,
            };
          }}
        />
        <AreaClosed
          data={data}
          fill={chartColor(variant)}
          yScale={performanceScale}
          curve={curveCatmullRom}
          x={d => dateScale(xAccessor(d))}
          y={d => performanceScale(yAccessor(d))}
          data-testid="placeholder-chart-area"
        />
      </Group>
    </svg>
  );
};
